import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import api from '../services/api';
import {
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentCheckIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

interface DashboardStats {
  summary: {
    totalEnquiries: number;
    totalQuotations: number;
    totalLogSheets: number;
    totalInvoices: number;
  };
  metrics: {
    pendingEnquiries: number;
    activeQuotations: number;
    pendingLogSheets: number;
    pendingInvoices: number;
  };
  recentActivity: {
    enquiries: any[];
    quotations: any[];
    logSheets: any[];
    invoices: any[];
  };
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await api.get('/dashboard/stats');
        if (cancelled) return;
        setStats(response.data);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching dashboard stats:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStats();
    return () => { cancelled = true; };
  }, [user]);

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Administrator';
      case 'MANAGER':
        return 'Manager';
      case 'FIELD_TEAM':
        return 'Field Team';
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading dashboard...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 overflow-y-auto h-full">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <h2 className="text-2xl font-bold mb-2">Welcome back, {user?.name}!</h2>
          <p className="text-blue-100">You are logged in as <span className="font-semibold">{getRoleDisplayName(user?.role || '')}</span></p>
        </div>

        {/* Summary Cards */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Enquiries</p>
                  <p className="text-3xl font-bold text-blue-600">{stats?.summary.totalEnquiries || 0}</p>
                </div>
                <ClipboardDocumentListIcon className="w-12 h-12 text-blue-500 opacity-20" />
              </div>
            </div>

            {user?.role !== 'FIELD_TEAM' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Quotations</p>
                    <p className="text-3xl font-bold text-green-600">{stats?.summary.totalQuotations || 0}</p>
                  </div>
                  <DocumentTextIcon className="w-12 h-12 text-green-500 opacity-20" />
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Log Sheets</p>
                  <p className="text-3xl font-bold text-purple-600">{stats?.summary.totalLogSheets || 0}</p>
                </div>
                <ClipboardDocumentCheckIcon className="w-12 h-12 text-purple-500 opacity-20" />
              </div>
            </div>

            {user?.role === 'ADMIN' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Invoices</p>
                    <p className="text-3xl font-bold text-orange-600">{stats?.summary.totalInvoices || 0}</p>
                  </div>
                  <DocumentDuplicateIcon className="w-12 h-12 text-orange-500 opacity-20" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Pending Enquiries</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats?.metrics.pendingEnquiries || 0}</p>
                </div>
                <ExclamationCircleIcon className="w-12 h-12 text-yellow-500 opacity-20" />
              </div>
            </div>

            {user?.role !== 'FIELD_TEAM' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Active Quotations</p>
                    <p className="text-3xl font-bold text-green-600">{stats?.metrics.activeQuotations || 0}</p>
                  </div>
                  <CheckCircleIcon className="w-12 h-12 text-green-500 opacity-20" />
                </div>
              </div>
            )}

            {user?.role !== 'FIELD_TEAM' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Pending Log Sheets</p>
                    <p className="text-3xl font-bold text-blue-600">{stats?.metrics.pendingLogSheets || 0}</p>
                  </div>
                  <ClockIcon className="w-12 h-12 text-blue-500 opacity-20" />
                </div>
              </div>
            )}

            {user?.role === 'ADMIN' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Pending Invoices</p>
                    <p className="text-3xl font-bold text-red-600">{stats?.metrics.pendingInvoices || 0}</p>
                  </div>
                  <CurrencyDollarIcon className="w-12 h-12 text-red-500 opacity-20" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Recent Enquiries - All roles */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h4 className="text-md font-semibold text-gray-800 mb-4">Recent Enquiries</h4>
              <div className="space-y-3">
                {stats?.recentActivity.enquiries && stats.recentActivity.enquiries.length > 0 ? (
                  stats.recentActivity.enquiries.map((enquiry: any) => (
                    <div key={enquiry.id} className="flex justify-between items-start border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{enquiry.enquiry_no}</div>
                        <div className="text-sm text-gray-600">{enquiry.contact_person}</div>
                        <div className="text-xs text-gray-500">{enquiry.contact_no}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          {enquiry.service_date ? new Date(enquiry.service_date).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className={`text-xs font-medium mt-1 ${
                          enquiry.is_confirmed === 'Y' ? 'text-green-600' : 'text-yellow-600'
                        }`}>
                          {enquiry.is_confirmed === 'Y' ? 'Confirmed' : 'Pending'}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 text-center py-4">No recent enquiries</div>
                )}
              </div>
            </div>

            {/* Recent Quotations - ADMIN and MANAGER only */}
            {user?.role !== 'FIELD_TEAM' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Recent Quotations</h4>
                <div className="space-y-3">
                  {stats?.recentActivity.quotations && stats.recentActivity.quotations.length > 0 ? (
                    stats.recentActivity.quotations.map((quotation: any) => (
                      <div key={quotation.id} className="flex justify-between items-start border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{quotation.quote_no}</div>
                          <div className="text-sm text-gray-600">Enq: {quotation.enquiry?.enquiry_no}</div>
                          <div className="text-xs text-gray-500">{quotation.enquiry?.contact_person}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            {quotation.quote_date ? new Date(quotation.quote_date).toLocaleDateString() : 'N/A'}
                          </div>
                          <div className={`text-xs font-medium mt-1 ${
                            quotation.is_active === 'Y' ? 'text-green-600' : 'text-gray-600'
                          }`}>
                            {quotation.is_active === 'Y' ? 'Active' : 'Inactive'}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-4">No recent quotations</div>
                  )}
                </div>
              </div>
            )}

            {/* Recent Log Sheets - All roles */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h4 className="text-md font-semibold text-gray-800 mb-4">Recent Log Sheets</h4>
              <div className="space-y-3">
                {stats?.recentActivity.logSheets && stats.recentActivity.logSheets.length > 0 ? (
                  stats.recentActivity.logSheets.map((logSheet: any) => (
                    <div key={logSheet.id} className="flex justify-between items-start border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{logSheet.log_code}</div>
                        <div className="text-sm text-gray-600">Enq: {logSheet.enquiry?.enquiry_no}</div>
                        <div className="text-xs text-gray-500">{logSheet.enquiry?.contact_person}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          {logSheet.log_date ? new Date(logSheet.log_date).toLocaleDateString() : 'N/A'}
                        </div>
                        <div className="text-sm font-medium text-purple-600 mt-1">
                          {logSheet.no_of_feet || 0} ft
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-gray-500 text-center py-4">No recent log sheets</div>
                )}
              </div>
            </div>

            {/* Recent Invoices - ADMIN only */}
            {user?.role === 'ADMIN' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-800 mb-4">Recent Invoices</h4>
                <div className="space-y-3">
                  {stats?.recentActivity.invoices && stats.recentActivity.invoices.length > 0 ? (
                    stats.recentActivity.invoices.map((invoice: any) => (
                      <div key={invoice.id} className="flex justify-between items-start border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{invoice.invoice_no}</div>
                          <div className="text-sm text-gray-600">{invoice.logSheet?.enquiry?.contact_person}</div>
                          <div className="text-xs text-gray-500">{invoice.logSheet?.enquiry?.contact_no}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">
                            {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
                          </div>
                          <div className="text-sm font-medium text-green-600 mt-1">
                            ₹{invoice.net_amount?.toLocaleString() || '0'}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-4">No recent invoices</div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;